import { useRef, useState } from "react";
import { backendApi } from "../../api";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import { OCRResult, Polyp, POLYP_TYPES, LOCATIONS } from "./interfaces";
import { useGetRecommendations } from "./queries";

const getLines = (ocrResult: OCRResult): string => {
  return ocrResult.text_lines.map((line) => line.text).join("\n");
};

const PolypTable = ({
  polyps,
  setPolyps,
}: {
  polyps: Polyp[];
  setPolyps: (polyps: Polyp[]) => void;
}) => {
  const handlePolypChange = (
    index: number,
    field: keyof Polyp,
    value: string | number | boolean
  ) => {
    const newPolyps = [...polyps];
    newPolyps[index] = { ...newPolyps[index], [field]: value };
    setPolyps(newPolyps);
  };

  const addNewPolyp = () => {
    setPolyps([
      ...polyps,
      {
        polypType: POLYP_TYPES[0],
        size: 1,
        location: LOCATIONS[0],
        dysplasia: false,
        piecemeal: false,
      },
    ]);
  };

  const deletePolyp = (index: number) => {
    setPolyps(polyps.filter((_, i) => i !== index));
  };

  return (
    <div className="overflow-x-auto">
      <table className="table table-xs">
        <thead>
          <tr>
            <th>Polyp Type</th>
            <th>Size</th>
            <th>Location</th>
            <th>Dysplasia</th>
            <th>Piecemeal</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {polyps.map((polyp, index) => (
            <tr key={index}>
              <td>
                <select
                  className="select select-xs sm:select-sm w-full max-w-xs"
                  value={polyp.polypType}
                  onChange={(e) =>
                    handlePolypChange(index, "polypType", e.target.value)
                  }
                >
                  {POLYP_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="number"
                  min="1"
                  className="input input-xs sm:input-sm w-16 sm:w-20"
                  value={polyp.size}
                  onChange={(e) =>
                    handlePolypChange(index, "size", e.target.value)
                  }
                  onBlur={(e) =>
                    handlePolypChange(
                      index,
                      "size",
                      Math.max(1, parseInt(e.target.value) || 1)
                    )
                  }
                />
              </td>
              <td>
                <select
                  className="select select-xs sm:select-sm w-full max-w-xs"
                  value={polyp.location}
                  onChange={(e) =>
                    handlePolypChange(index, "location", e.target.value)
                  }
                >
                  {LOCATIONS.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={polyp.dysplasia}
                  onChange={(e) =>
                    handlePolypChange(index, "dysplasia", e.target.checked)
                  }
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={polyp.piecemeal}
                  onChange={(e) =>
                    handlePolypChange(index, "piecemeal", e.target.checked)
                  }
                />
              </td>
              <td>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => deletePolyp(index)}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="btn mt-4" onClick={addNewPolyp}>
        Add Polyp <PlusIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

const ColonoscopyFlow = () => {
  const [file1, setFile1] = useState<File | null>(null);
  const [file2, setFile2] = useState<File | null>(null);
  const [text1, setText1] = useState("");
  const [text2, setText2] = useState("");
  const [polyps, setPolyps] = useState<Polyp[]>([]);
  const [recommendationResult, setRecommendationResult] = useState<{
    recommendation: string;
    context: string;
    emails: { [key: string]: string };
  } | null>(null);
  const { mutate: getRecommendations } = useGetRecommendations((data) => {
    setRecommendationResult({
      recommendation: data.recommendation,
      context: data.context,
      emails: data.emails,
    });
    dialogRef.current?.showModal();
  });
  const dialogRef = useRef<HTMLDialogElement>(null);

  const handleFileChange1 = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile1(e.target.files?.[0] ?? null);
  };

  const handleFileChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile2(e.target.files?.[0] ?? null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData();
    const files = [file1, file2].filter(Boolean) as File[];
    files.forEach((file) => formData.append("files", file));

    try {
      const response = await backendApi<OCRResult[][]>({
        url: "/ocr/predict",
        method: "POST",
        data: formData,
      });
      // console.log(response.data);
      if (response.data[0]) {
        setText1(getLines(response.data[0][0]));
      }
      if (response.data[1]) {
        setText2(getLines(response.data[1][0]));
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };

  return (
    <div className="p-6 bg-base-200 rounded-lg shadow-lg mb-6">
      <h1 className="text-3xl font-bold mb-4">Colonoscopy Flow</h1>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="form-control">
          <label className="label">
            <span className="label-text">Upload colonoscopy report:</span>
          </label>
          <input
            type="file"
            onChange={handleFileChange1}
            className="file-input file-input-bordered w-full"
          />
        </div>
        <div className="form-control">
          <label className="label">
            <span className="label-text">Upload pathology report:</span>
          </label>
          <input
            type="file"
            onChange={handleFileChange2}
            className="file-input file-input-bordered w-full"
          />
        </div>
        <button type="submit" className="btn btn-primary w-full">
          Submit
        </button>
      </form>
      <div className="mt-6">
        <h2 className="text-xl font-semibold mb-2">Extracted Text 1:</h2>
        <textarea
          value={text1}
          onChange={(e) => setText1(e.target.value)} // Add this line to handle changes
          className="textarea textarea-bordered w-full h-24"
        />
      </div>
      <div className="mt-6">
        <h2 className="text-xl font-semibold mb-2">Extracted Text 2:</h2>
        <textarea
          value={text2}
          onChange={(e) => setText2(e.target.value)} // Add this line to handle changes
          className="textarea textarea-bordered w-full h-24"
        />
      </div>
      <div className="mt-6">
        <h2 className="text-xl font-semibold mb-2">Polyps:</h2>
        <PolypTable polyps={polyps} setPolyps={setPolyps} />
      </div>
      <button
        className={
          "btn btn-primary mt-4" + (polyps.length === 0 ? " btn-disabled" : "")
        }
        onClick={() => polyps.length > 0 && getRecommendations(polyps)}
      >
        Get Recommendations
      </button>
      <dialog ref={dialogRef} className="modal">
        <div className="modal-box">
          <form method="dialog">
            {/* if there is a button in form, it will close the modal */}
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
              ✕
            </button>
          </form>
          <h3 className="font-bold text-lg">Recommendation:</h3>
          <p className="py-4 whitespace-pre-line">
            {recommendationResult?.recommendation}
          </p>
          <p className="py-4 whitespace-pre-line">
            Context: <br />
            {recommendationResult?.context}
          </p>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </div>
  );
};

export default ColonoscopyFlow;
